import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachArrow from "./wrapper/EachArrow";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Arrow extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawArrow = this.handleDrawArrow.bind(this);
    this.handleEndDrawArrow = this.handleEndDrawArrow.bind(this);
    this.handleDragArrow = this.handleDragArrow.bind(this);
    this.handleDragArrowComplete = this.handleDragArrowComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("arrows").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragArrow(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragArrowComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { arrows } = this.props;
      const newArrows = arrows.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x: override.x,
              y: override.y,
              sx: override.sx !== undefined ? override.sx : each.sx,
              sy: override.sy !== undefined ? override.sy : each.sy,
              angle: override.angle !== undefined ? override.angle : each.angle,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newArrows, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState(
        {
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    } else {
      this.setState(
        {
          current: {
            x: xyValue[0],
            y: xyValue[1],
            x2: null,
            y2: null,
            sx: 1,
            sy: 1,
            angle: 0,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawArrow(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleEndDrawArrow(xyValue, moreProps, e) {
    const { arrows, appearance } = this.props;
    const { current } = this.state;

    if (!isDefined(current) || !isDefined(current.x)) {
      return;
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Calculate scale based on drag distance
    const x2 = xyValue[0];
    const y2 = xyValue[1];

    // Default scale if just clicked (no drag)
    let sx = 1;
    let sy = 1;
    let angle = 0;

    // If dragged, calculate scale from pixel distance
    if (current.x2 !== null && current.y2 !== null) {
      const centerX = xScale(current.x);
      const centerY = yScale(current.y);
      const x2Pixel = xScale(x2);
      const y2Pixel = yScale(y2);

      const dx = x2Pixel - centerX;
      const dy = y2Pixel - centerY;

      // CRITICAL: Use same formula as resize handle - baseDim = width, divide by (baseDim / 2)
      const distance = Math.sqrt(dx * dx + dy * dy);
      const baseDim = 11; // dimension[0] - must match helper method
      if (distance > 5) {
        sx = Math.max(0.2, distance / (baseDim / 2));
        sy = sx; // Keep proportional initially
      }
    }

    const newArrows = [
      ...arrows.map((d) => ({ ...d, selected: false })),
      {
        x: current.x,
        y: current.y,
        // Don't store x2/y2 - they're only for initial drawing
        sx,
        sy,
        angle,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newArrows, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, arrows, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempArrow =
      isDefined(current) && isDefined(current.x) ? (
        <EachArrow
          interactive={false}
          x={current.x}
          y={current.y}
          x2={current.x2}
          y2={current.y2}
          sx={current.sx}
          sy={current.sy}
          angle={current.angle}
          stroke={
            appearance.stroke && typeof appearance.stroke === "object"
              ? appearance.stroke
              : {
                  color: appearance.stroke || "#000000",
                  width: 2,
                  opacity: 1,
                  dash: "Solid",
                }
          }
          fill={
            appearance.fill && typeof appearance.fill === "object"
              ? appearance.fill
              : { color: appearance.fill || "#000000", opacity: 0.7 }
          }
        />
      ) : null;

    return (
      <g>
        {arrows.map((each, idx) => {
          let eachAppearance = appearance;
          if (isDefined(each.appearance)) {
            eachAppearance = { ...appearance, ...each.appearance };
          }

          const hoverTextWithDefault = {
            ...Arrow.defaultProps.hoverText,
            ...hoverText,
          };

          return (
            <EachArrow
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={each.selected}
              x={getValueFromOverride(override, idx, "x", each.x)}
              y={getValueFromOverride(override, idx, "y", each.y)}
              sx={getValueFromOverride(override, idx, "sx", each.sx)}
              sy={getValueFromOverride(override, idx, "sy", each.sy)}
              angle={getValueFromOverride(override, idx, "angle", each.angle)}
              stroke={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke
                  : {
                      color: eachAppearance.stroke || "#000000",
                      width: 2,
                      opacity: 1,
                      dash: "Solid",
                    }
              }
              fill={
                eachAppearance.fill && typeof eachAppearance.fill === "object"
                  ? eachAppearance.fill
                  : { color: eachAppearance.fill || "#000000", opacity: 0.7 }
              }
              onDrag={this.handleDragArrow}
              onDragComplete={this.handleDragArrowComplete}
              hoverText={hoverTextWithDefault}
            />
          );
        })}
        {tempArrow}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawArrow}
          onClick={this.handleEndDrawArrow}
          data-evo-id="interactive-arrow"
        />
      </g>
    );
  }
}

Arrow.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  snap: PropTypes.bool,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  hoverText: PropTypes.object,

  arrows: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
    ]),
    fill: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
    ]),
  }).isRequired,
};

Arrow.defaultProps = {
  onStart: noop,
  onComplete: noop,
  onSelect: noop,
  shouldDisableSnap: () => true,
  snap: false,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 4,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },

  arrows: [],

  appearance: {
    stroke: {
      color: "#2196F3",
      width: 2,
      opacity: 1,
      dash: "Solid",
    },
    fill: {
      color: "#2196F3",
      opacity: 0.7,
    },
  },
};

export default Arrow;
